import { FC, ReactNode } from 'react';
import { Box, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import oberthurIsotype from '../../assets/oberthur-isotype.png';
import { colors, radii } from '../../theme/tokens';

// `sx` can be an object, a function or an array; MUI's array form
// composes them all without losing any.
const mergeSx = (
  base: SxProps<Theme>,
  extra?: SxProps<Theme>,
): SxProps<Theme> => [
  ...(Array.isArray(base) ? base : [base]),
  ...(Array.isArray(extra) ? extra : [extra]),
];

// Brand pieces shared by the application header and by the login.
// None of them carries a fixed colour: they inherit `color` from the container
// (white over the login blue, blue/base text over the application's light background).

/** `assets/oberthur-isotype.png` is 240×286 with transparency. */
const OBERTHUR_LOGO_RATIO = 240 / 286;

/**
 * Oberthur isotype (the script "O" with its four squares, no wordmark).
 * The colour artwork reads over light backgrounds; `onPrimary` renders it as
 * a white silhouette for the blue brand screens, where the XD draws it
 * knocked out.
 */
export const OberthurLogo: FC<{
  height?: number;
  /** White silhouette, for the blue backgrounds. */
  onPrimary?: boolean;
  sx?: SxProps<Theme>;
}> = ({ height = 36, onPrimary = false, sx }) => (
  <Box
    component="img"
    src={oberthurIsotype}
    alt="Oberthur Solutions"
    sx={mergeSx(
      {
        height,
        width: Math.round(OBERTHUR_LOGO_RATIO * height),
        display: 'inline-block',
        ...(onPrimary && { filter: 'brightness(0) invert(1)' }),
      },
      sx,
    )}
  />
);

/**
 * The XD "Logo Client" slot: the platform is white-label and the design
 * reserves a square next to the Oberthur logo. Light grey square with a
 * grey image placeholder, until each client supplies its own logo.
 */
export const ClientLogoPlaceholder: FC<{ height?: number }> = ({
  height = 36,
}) => (
  <Box
    role="img"
    aria-label="Logo Client"
    sx={{
      height,
      width: height,
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: `${radii.card}px`,
      backgroundColor: colors.surface,
      color: colors.hint,
    }}
  >
    <ImageOutlinedIcon sx={{ fontSize: height * 0.55 }} />
  </Box>
);

/** Oberthur logo · vertical separator · "Logo Client" slot. */
export const LogoLockup: FC<{
  height?: number;
  /** Forwarded to the logo: white silhouette on the blue screens. */
  onPrimary?: boolean;
  sx?: SxProps<Theme>;
}> = ({ height = 36, onPrimary = false, sx }) => (
  <Box sx={mergeSx({ display: 'flex', alignItems: 'center', gap: 2 }, sx)}>
    <OberthurLogo height={height} onPrimary={onPrimary} />
    <Box
      aria-hidden
      sx={{
        width: '1px',
        height,
        backgroundColor: 'currentColor',
        opacity: 0.4,
      }}
    />
    <ClientLogoPlaceholder height={height} />
  </Box>
);

/**
 * "archiv.OS" (h1, serif) over "par Oberthur Solutions".
 *
 * The XD only defines two serif sizes (20 and 60). The login title uses the
 * 60 px of `.titre-app`; in the application header the same lockup is
 * clearly smaller, and since there is no intermediate token it is set via `sx`
 * from the caller (`titleSx`).
 */
export const BrandTitle: FC<{
  component?: 'h1' | 'h2' | 'div';
  titleSx?: SxProps<Theme>;
  subtitleSx?: SxProps<Theme>;
}> = ({ component = 'h1', titleSx, subtitleSx }) => (
  <Box>
    <Typography
      component={component}
      variant="h1"
      sx={mergeSx({ color: 'inherit' }, titleSx)}
    >
      archiv.OS
    </Typography>
    <Typography
      component="p"
      variant="body2"
      sx={mergeSx({ color: 'inherit' }, subtitleSx)}
    >
      par Oberthur Solutions
    </Typography>
  </Box>
);

/**
 * Full concentric circles centred in their own box, for the ornaments the XD
 * anchors to an edge rather than a corner (the large half-visible rings on
 * the right edge of every screen) and for the small "bullseye" accents. The
 * caller positions the box via `sx`; a clipping host hides what sticks out.
 */
export const ConcentricRings: FC<{
  /** Radius of the outer ring, in px (the box is a 2×size square). */
  size: number;
  opacity?: number;
  color?: string;
  position?: 'absolute' | 'fixed';
  sx?: SxProps<Theme>;
}> = ({ size, opacity = 1, color = colors.accent, position = 'absolute', sx }) => {
  const box = size * 2;
  const radii_ = [0.2, 0.36, 0.52, 0.68, 0.84, 1].map((k) => k * size);

  return (
    <Box
      component="svg"
      aria-hidden
      viewBox={`0 0 ${box} ${box}`}
      sx={mergeSx(
        {
          position,
          width: box,
          height: box,
          pointerEvents: 'none',
          zIndex: 0,
          opacity,
          color,
        },
        sx,
      )}
    >
      {radii_.map((r) => (
        <circle
          key={r}
          cx={size}
          cy={size}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
        />
      ))}
    </Box>
  );
};

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * XD ornament: thin concentric circles in orange, anchored to a corner.
 * Inline SVG, with no pointer events and below the content (`zIndex: 0`), so
 * that it never gets in the way. The container that hosts it must be
 * `position: relative` (or `fixed` if `fixed` is used) with `overflow:
 * hidden` to clip the circles that overflow.
 */
export const CornerCircles: FC<{
  corner: Corner;
  /** Radius of the outer circle, in px. */
  size?: number;
  opacity?: number;
  color?: string;
  position?: 'absolute' | 'fixed';
  /** To adjust `width`/`height` per breakpoint: the drawing scales with them. */
  sx?: SxProps<Theme>;
}> = ({
  corner,
  size = 520,
  opacity = 1,
  color = colors.accent,
  position = 'absolute',
  sx,
}) => {
  const [vertical, horizontal] = corner.split('-') as [
    'top' | 'bottom',
    'left' | 'right',
  ];
  // The centre of the circles is the corner itself; they are drawn in the
  // quadrant that stays inside the screen and are flipped according to the corner.
  const flipX = horizontal === 'right' ? -1 : 1;
  const flipY = vertical === 'top' ? -1 : 1;
  const radii_ = [0.2, 0.36, 0.52, 0.68, 0.84, 1].map((k) => k * size);

  return (
    <Box
      component="svg"
      aria-hidden
      viewBox={`0 0 ${size} ${size}`}
      sx={mergeSx(
        {
          position,
          [vertical]: 0,
          [horizontal]: 0,
          width: size,
          height: size,
          transform: `scale(${flipX}, ${flipY})`,
          pointerEvents: 'none',
          zIndex: 0,
          opacity,
          color,
        },
        sx,
      )}
    >
      {radii_.map((r) => (
        <circle
          key={r}
          cx={0}
          cy={size}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
        />
      ))}
    </Box>
  );
};

/**
 * Brand screen: XD screen 1 (Login) without its form. Full-screen blue
 * background, the XD ornament — a circle cluster in the lower-left corner,
 * large rings half-visible on the right edge and two small bullseye accents —
 * and a centred block with the logo lockup, "archiv.OS" and whatever each
 * screen puts below (`children`). The artboard centres the lockup and the
 * title; the form below keeps its own alignment.
 *
 * Shared by Login, public verification, "Nouveau mot de passe" and the error
 * screens: the four that carry neither header nor navigation. Everything
 * inside inherits `color: white`.
 *
 * Mobile (ROADMAP.md, Part 2, "Login"): reduced circles clipped in the
 * corners, no accents, full-width block with a margin.
 */
const BRAND_SCREEN_MOBILE_CIRCLES_SX = (theme: Theme) => ({
  [theme.breakpoints.down('tablet')]: { width: 200, height: 200 },
});

const DESKTOP_ONLY_SX = (theme: Theme) => ({
  [theme.breakpoints.down('tablet')]: { display: 'none' },
});

/** The XD ornament composition, shared by BrandScreen and the app shell. */
export const BrandOrnament: FC<{
  position?: 'absolute' | 'fixed';
  opacity?: number;
  color?: string;
}> = ({ position = 'absolute', opacity = 1, color }) => (
  <>
    <CornerCircles
      corner="bottom-left"
      size={480}
      position={position}
      opacity={opacity}
      color={color}
      sx={BRAND_SCREEN_MOBILE_CIRCLES_SX}
    />
    {/* The mobile screens (smartphone XD, S1/S2) carry the ornament only in
        the lower-left corner: the right-edge rings are desktop-only. */}
    <ConcentricRings
      size={420}
      position={position}
      opacity={opacity}
      color={color}
      sx={(theme: Theme) => ({
        ...DESKTOP_ONLY_SX(theme),
        right: -420,
        top: '50%',
        transform: 'translateY(-50%)',
      })}
    />
    <ConcentricRings
      size={36}
      position={position}
      opacity={opacity}
      color={color}
      sx={(theme: Theme) => ({
        ...DESKTOP_ONLY_SX(theme),
        right: 96,
        top: '46%',
      })}
    />
    {/* The small lower-left bullseye stays on mobile, tucked over the
        corner cluster as S1 draws it. `bottom` is fixed to the viewport
        edge while BrandScreen's content block is vertically centred, so a
        shorter block (RetrievePassword's expired state, say) leaves less
        clearance below it than Login's full form does. Anchored low enough
        to clear even the shortest BrandScreen content instead of Login's
        specifically, so it never sits under a link or button on any of
        them — found auditing the mobile screens the smartphone XD does not
        cover (M-3 in DESIGN.md). */}
    <ConcentricRings
      size={30}
      position={position}
      opacity={opacity}
      color={color}
      sx={(theme: Theme) => ({
        left: 48,
        bottom: 280,
        [theme.breakpoints.down('tablet')]: { left: 16, bottom: 60 },
      })}
    />
  </>
);

export const BrandScreen: FC<{
  /** Maximum width of the centred block, in px. */
  maxWidth?: number;
  children: ReactNode;
}> = ({ maxWidth = 440, children }) => {
  // The mobile XD draws a taller lockup than desktop's.
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('tablet'),
  );

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.primary,
        color: colors.white,
      }}
    >
      <BrandOrnament />

      <Box
        sx={(theme) => ({
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth,
          px: 3,
          py: 6,
          [theme.breakpoints.down('tablet')]: {
            maxWidth: 'none',
            px: 2.5,
            py: 4,
          },
        })}
      >
        <LogoLockup
          height={isMobile ? 64 : 40}
          onPrimary
          sx={{ justifyContent: 'center' }}
        />

        <BrandTitle
          component="h1"
          titleSx={(theme) => ({
            mt: 4,
            textAlign: 'center',
            [theme.breakpoints.down('tablet')]: {
              fontSize: '2.5rem',
              lineHeight: 1,
            },
          })}
          subtitleSx={{ mt: 1, opacity: 0.85, textAlign: 'center' }}
        />

        {children}
      </Box>
    </Box>
  );
};
